import React, {useEffect, useMemo, useRef} from "react";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {Dropdown} from "primereact/dropdown";
import {useAppDispatch, useAppSelector} from "redux/hooks";
import {classNames} from "primereact/utils";
import {setTrunkField} from "../../../../redux/actions/actionsTrunk";
import {InputSwitch} from "primereact/inputswitch";
import {Divider} from "primereact/divider";
import {getUsersManagerUserBranchesData} from "../../../../redux/api/apiUsersList"
function normalizeIdForDropdown(
    raw: unknown,
    options: {id?: unknown}[] | null | undefined
): number | string | null {
    if (raw === null || raw === undefined || raw === "") return null;
    if (!options?.length) {
        const n = Number(raw);
        return Number.isNaN(n) ? (raw as string) : n;
    }
    const sampleId = options[0]?.id;
    if (typeof sampleId === "number") {
        const n = Number(raw);
        return Number.isNaN(n) ? null : n;
    }
    if (typeof sampleId === "string") {
        return String(raw);
    }
    const n = Number(raw);
    return Number.isNaN(n) ? (raw as string) : n;
}

type TrunkOption = {id: number | string; name: string};

function mapRowsToDefaultTrunkOptions(rows: unknown): TrunkOption[] {
    if (!Array.isArray(rows)) return [];
    const out: TrunkOption[] = [];
    for (const item of rows) {
        if (item == null) continue;
        if (typeof item === "number") {
            out.push({id: item, name: String(item)});
            continue;
        }
        if (typeof item !== "object") continue;
        const o = item as Record<string, unknown>;
        const id =
            o.defaultTrunkId ??
            o.DefaultTrunkId ??
            o.id ??
            o.trunkId ??
            o.TrunkId ??
            o.sofiaTrunkId;
        if (id === undefined || id === null || id === "") continue;
        const name =
            (typeof o.gatewayName === "string" && o.gatewayName) ||
            (typeof o.name === "string" && o.name) ||
            (typeof o.trunkName === "string" && o.trunkName) ||
            `Транк ${id}`;
        out.push({id: id as number | string, name});
    }
    return out;
}

const TrunkList = () => {
    const dispatch = useAppDispatch();
    const trunkData = useAppSelector(state => state.TrunksListReducer.trunkData);
    const defaultTrunkSettingsList = useAppSelector(
        state => state.TrunksListReducer.defaultTrunkSettingsList
    );
    const trunksList = useAppSelector(state => state.TrunksListReducer.trunksList);
    const errorFields = useAppSelector(state => state.TrunksListReducer.errorFields);
    const nameField = useRef(null);
    const editType = useAppSelector(state => state.UsersReducer.editType);
    const archiveTableToggle = useAppSelector(state => state.TrunksListReducer.archiveTrunkTableToggle);
    const jwtToken = useAppSelector(state => state.UsersReducer.userData.jwtToken);
    const branches = useAppSelector(state => state.UsersListReducer.branches);

    const rawBranchId =
        trunkData?.branchId ??
        trunkData?.BranchId ??
        trunkData?.mainBranchId ??
        null;
    const branchIdValue = normalizeIdForDropdown(rawBranchId, branches);

    const defaultTrunkOptions = useMemo(() => {
        const fromDefaults = mapRowsToDefaultTrunkOptions(defaultTrunkSettingsList);
        if (fromDefaults.length > 0) return fromDefaults;
        if (Array.isArray(trunksList) && trunksList.length > 0) {
            return trunksList
                .map((row: {id?: number; name?: string}) => {
                    if (row?.id == null) return null;
                    return {
                        id: row.id,
                        name: row.name ?? String(row.id),
                    } as TrunkOption;
                })
                .filter((x): x is TrunkOption => x != null);
        }
        return [];
    }, [defaultTrunkSettingsList, trunksList]);

    const rawDefaultTrunkId = trunkData?.defaultTrunkId ?? null;
    const defaultTrunkIdValue = normalizeIdForDropdown(
        rawDefaultTrunkId,
        defaultTrunkOptions
    );

    useEffect(() => {
        dispatch(getUsersManagerUserBranchesData(jwtToken?.jwtToken));
    }, [dispatch, jwtToken?.jwtToken]);

    const scrollIntoNameView = () => {
        // @ts-ignore
        nameField?.current?.scrollIntoView({behavior: 'smooth'});
    };

    useEffect(() => {
        if (!trunkData.name && errorFields?.name) {
            scrollIntoNameView();
        }
        if (
            errorFields?.nameUniq ||
            errorFields?.branchId ||
            errorFields?.defaultTrunkId
        ) {
            scrollIntoNameView();
        }
    }, [errorFields, trunkData]);

    return (
        <div className="general-settings">
            <div className="grid p-fluid">
                <div className="field col-12 md:col-6" ref={nameField}>
                    <label htmlFor="name" className={classNames('', {'p-error': errorFields?.name || errorFields?.nameUniq})}>Назва транка *</label>
                    <InputText
                        id="name"
                        value={trunkData?.name || ''}
                        maxLength={150}
                        className={classNames('', {'p-invalid': errorFields?.name || errorFields?.nameUniq})}
                        onChange={e => {
                            dispatch(setTrunkField({section: 'trunk', fieldName: 'name', value: e.target.value}));
                        }}
                        disabled={!archiveTableToggle || !editType}
                    />
                    {errorFields?.nameUniq && <div className="col-12 md:col-12 pt-2 px-0 pb-0"><span className="title p-error text-sm">{errorFields?.nameUniq}*</span></div>}
                </div>
                <div className="field col-12 md:col-3 m-0 p-0">
                </div>
                <div className="field col-12 md:col-6">
                    <label htmlFor="description">Коментар</label>
                    <InputTextarea
                        id="description"
                        value={trunkData?.description || ''}
                        onChange={e => {
                            dispatch(setTrunkField({section: 'trunk', fieldName: 'description', value: e.target.value}));
                        }}
                        autoResize
                        rows={3}
                        maxLength={500}
                        disabled={!archiveTableToggle || !editType}
                    />
                </div>
                <Divider className="mx-2"/>
                <div className="field col-12 md:col-4">
                    <label
                        htmlFor="trunkBranchId"
                        className={classNames("", {
                            "p-error": errorFields?.branchId && !branchIdValue,
                        })}
                    >
                        Бранч *
                    </label>
                    <Dropdown
                        id="trunkBranchId"
                        value={branchIdValue}
                        options={branches}
                        filter
                        className={classNames("w-full", {
                            "p-invalid": errorFields?.branchId && !branchIdValue,
                        })}
                        optionValue="id"
                        optionLabel="name"
                        placeholder="Виберіть бранч"
                        onChange={(e) => {
                            dispatch(
                                setTrunkField({
                                    section: "trunk",
                                    fieldName: "branchId",
                                    value: e.value,
                                })
                            );
                        }}
                        disabled={!archiveTableToggle || !editType}
                    />
                    {errorFields?.branchId && (
                        <div className="col-12 md:col-12 pt-2 px-0 pb-0">
                            <span className="title p-error text-sm">
                                {(errorFields.branchId as { branchId?: string })?.branchId}*
                            </span>
                        </div>
                    )}
                </div>
                <Divider className="mx-2"/>
                <div className="field col-12 md:col-12">
                    <label htmlFor="name" className="pl-2">Стан</label>
                    <div className="field-radiobutton my-4 pl-1">
                        <label htmlFor="name" className="mr-2 w-6rem">Не активна</label>
                        <InputSwitch
                            disabled={!archiveTableToggle || !editType}
                            className="mr-3"
                            checked={trunkData.isActive} onChange={(e) => {
                            dispatch(setTrunkField({section: 'trunk', fieldName: 'isActive', value: e.value}));
                        }}/>
                        <label htmlFor="icon">Активна</label>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TrunkList;
